import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Menu, MenuItem, Typography, Divider, ListItemText, Tooltip, TextField, InputAdornment, Box, ListItemIcon
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  CalendarToday, Schedule, Event, Today, AccessTime, Folder, Description,
  Fingerprint, Assignment, TextSnippet, SummarizeOutlined, CreateNewFolder,
  EditNote, CheckCircle, Update, Extension, Person, LocationOn, AttachMoney,
  Message, Numbers, Flag, Category, LocalOffer, Image
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useActionScope } from './Actions/ActionScopeContext';
import { systemTagsEvent, systemTagsGlobal } from '../constants/systemTags';
import { buildReservedTagSet } from '../Utils/tagUtils';

const TagsDropdown = ({
  tags,
  handleTagSelect,
  anchorEl,
  setAnchorEl,
  additionalTags = [],
  scope
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const tooltipCacheRef = useRef(new Map()); // Cache tooltip content so we don't recompute on every hover
  const contextScope = useActionScope();
  const effectiveScope = scope || contextScope || 'event';
  const hideEventTags = effectiveScope === 'project';

  // Keep focus on search input when menu is open
  useEffect(() => {
    if (anchorEl && searchInputRef.current) {
      // Small delay to ensure menu is rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [anchorEl]);

  // Icon mapping function for string-based icon names from backend
  const getIconComponent = (iconName) => {
    const iconMap = {
      // Material-UI icon component names
      'CalendarToday': CalendarToday,
      'Schedule': Schedule,
      'Event': Event,
      'Today': Today,
      'AccessTime': AccessTime,
      'Folder': Folder,
      'Description': Description,
      'Fingerprint': Fingerprint,
      'Assignment': Assignment,
      'TextSnippet': TextSnippet,
      'SummarizeOutlined': SummarizeOutlined,
      'CreateNewFolder': CreateNewFolder,
      'EditNote': EditNote,
      'CheckCircle': CheckCircle,
      'Update': Update,
      'Extension': Extension,
      'Person': Person,
      'LocationOn': LocationOn,
      'AttachMoney': AttachMoney,
      'Message': Message,
      'Numbers': Numbers,
      'Flag': Flag,
      'Category': Category,
      'LocalOffer': LocalOffer,
      'Image': Image,
      // File-based icon mappings from backend
      'doctor.png': Person,
      'nurse.png': Person,
      'sparkling.png': SummarizeOutlined,
      'licence.png': Assignment,
      'validation.png': CheckCircle,
      'document.svg': Description,
      'document.png': Description
    };
    return iconMap[iconName] || LocalOffer; // Default to LocalOffer if icon not found
  };

  // Function to get icon for custom tags
  const getCustomTagIcon = (tag) => {
    // If the tag has an icon property (like from TimeEventTag), use it
    if (tag?.icon) {
      // Handle both string and component icons
      if (typeof tag.icon === 'string') {
        return getIconComponent(tag.icon);
      } else {
        return tag.icon;
      }
    }
    
    // Check for image type tags first
    if (tag?.type === 'image') {
      return Image;
    }
    
    // Fallback icon based on tag properties or content
    const value = getTagValue(tag).toLowerCase();
    const description = (tag?.description || '').toLowerCase();
    
    // Try to match by common patterns
    if (value.includes('date') || value.includes('time') || description.includes('date') || description.includes('time')) {
      return CalendarToday;
    }
    if (value.includes('file') || description.includes('file')) {
      return Description;
    }
    if (value.includes('id') || description.includes('identifier')) {
      return Fingerprint;
    }
    if (value.includes('text') || description.includes('text')) {
      return TextSnippet;
    }
    if (value.includes('image') || description.includes('image')) {
      return Image;
    }
    
    // Default icon for custom tags
    return LocalOffer;
  };
  const systemTags = useMemo(() => {
    if (hideEventTags) {
      return [...systemTagsGlobal];
    }
    return [
      ...systemTagsGlobal,
      ...systemTagsEvent
    ];
  }, [hideEventTags, systemTagsEvent, systemTagsGlobal]);

  const reservedTagValues = useMemo(
    () => buildReservedTagSet(systemTagsEvent, systemTagsGlobal),
    [systemTagsEvent, systemTagsGlobal]
  );

  const defaultTags = useMemo(() => [
    ...systemTags,
    ...additionalTags
  ], [systemTags, additionalTags]);

  const buildTooltipContent = useCallback((tag, displayLabel, currentValue) => {
    const description = tag?.description || '';
    const valueText = currentValue ? String(currentValue) : t('tags_dropdown.empty_value', 'No current value');
    if (!description) {
      return `${t('tags_dropdown.current_value', 'Current value')}: ${valueText}`;
    }
    return `${description}\n${t('tags_dropdown.current_value', 'Current value')}: ${valueText}`;
  }, [t]);

  const getTooltipContent = useCallback((tag, displayLabel) => {
    // Use the tag value/variable as cache key to avoid recomputing on hover
    const cacheKey = tag?.value || tag?.variable || tag?.label || displayLabel;
    if (cacheKey && tooltipCacheRef.current.has(cacheKey)) {
      return tooltipCacheRef.current.get(cacheKey);
    }

    const tagValueRaw = tag?.reply ?? tag?.current_value ?? tag?.currentValue ?? tag?.value ?? tag?.variable ?? '';
    const tooltipText = buildTooltipContent(tag, displayLabel, tagValueRaw);

    if (cacheKey) {
      tooltipCacheRef.current.set(cacheKey, tooltipText);
    }
    return tooltipText;
  }, [buildTooltipContent]);


  const handleClose = () => {
    if (setAnchorEl) { // Ensure setAnchorEl is a function before calling
        setAnchorEl(null);
    }
  };

  // UPDATED getTagValue
  const getTagValue = useCallback((tag) => {
    // Prioritize 'variable', then 'value', then the tag itself if it's a string
    const valueToUse = tag?.variable || tag?.value || tag;
    return typeof valueToUse === 'string' ? valueToUse.trim() : "";
  }, []);

  const safeTags = useMemo(() => {
    if (tags) {
      if (typeof tags === 'string') {
        try {
          const parsed = JSON.parse(tags);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          // If parsing fails, and it's a string, treat as a single tag string
          return [tags];
        }
      } else if (Array.isArray(tags)) {
        return tags; // Already an array (e.g., from SettingSendAPI)
      } else if (typeof tags === 'object' && tags !== null) {
        return [tags]; // A single tag object
      }
    }
    return [];
  }, [tags]);

  // Categorize tags based on tag_type field
  const { timeeventTags, widgetTags } = useMemo(() => {
    const timeevent = [];
    const widget = [];
    
    safeTags.forEach(tag => {
      const normalizedValue = getTagValue(tag).toLowerCase();
      if (!normalizedValue || reservedTagValues.has(normalizedValue)) {
        return;
      }

      if (tag.tag_type === 'widget') {
        widget.push(tag);
      } else {
        // Default to timeevent for backward compatibility
        timeevent.push(tag);
      }
    });
    
    return { timeeventTags: timeevent, widgetTags: widget };
  }, [safeTags, getTagValue, reservedTagValues]);

  const filteredTimeeventTags = useMemo(() => {
    if (hideEventTags) {
      return [];
    }
    if (!searchQuery.trim()) {
      return timeeventTags;
    }
    const query = searchQuery.trim().toLowerCase();
    return timeeventTags.filter(tag => {
      const value = getTagValue(tag);
      const label = tag?.label || value;
      const description = tag?.description || "";
      return label.toLowerCase().includes(query) ||
        value.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query);
    });
  }, [timeeventTags, searchQuery, getTagValue, hideEventTags]);

  const filteredWidgetTags = useMemo(() => {
    if (!searchQuery.trim()) {
      return widgetTags;
    }
    const query = searchQuery.trim().toLowerCase();
    return widgetTags.filter(tag => {
      const value = getTagValue(tag);
      const label = tag?.label || value;
      const description = tag?.description || "";
      return label.toLowerCase().includes(query) ||
        value.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query);
    });
  }, [widgetTags, searchQuery, getTagValue]);

  const filteredDefaultTags = useMemo(() => {
    if (!searchQuery.trim()) {
      return defaultTags;
    }
    const query = searchQuery.trim().toLowerCase();
    return defaultTags.filter(tag => {
      return tag.label.toLowerCase().includes(query) ||
        tag.value.toLowerCase().includes(query) ||
        (tag.description && tag.description.toLowerCase().includes(query));
    });
  }, [defaultTags, searchQuery]);


  
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose} // This will call the setAnchorEl(null) from the parent
      autoFocus={false} // Keep this to prevent Menu itself from grabbing initial focus
      disableAutoFocusItem={true} // Keep this to prevent menu items from stealing focus
      disableEnforceFocus={true} // Allow focus to remain on the search input
      disableRestoreFocus={true} // Prevent focus restoration when menu closes
      MenuListProps={{
        onMouseDown: (e) => {
          // Prevent menu from closing when clicking inside
          if (e.target.closest('.search-container')) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }}
      PaperProps={{
        elevation: 3,
        onMouseDown: (e) => {
          // Prevent default behavior for clicks on the search area
          if (e.target.closest('.search-container')) {
            e.preventDefault();
          }
        },
        sx: {
          width: 320,
          maxHeight: 450,
          borderRadius: '8px',
          mt: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '4px' },
          '&::-webkit-scrollbar-track': { backgroundColor: '#f5f5f5' },
        },
      }}
    >
      <Box 
        className="search-container"
        sx={{ px: 2.5, py: 1.5, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.preventDefault()}
      >
        <TextField
          ref={searchInputRef}
          variant="outlined"
          placeholder={t('auto_create_file.search_tags') || "Search tags..."}
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(e) => {
            e.stopPropagation();
            setSearchQuery(e.target.value);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            // Ensure focus stays on the input
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#666' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: '8px', fontSize: '0.9rem' }
          }}
        />
      </Box>

      {/* CUSTOM TAGS (TimeEvent) - Now displayed first */}
      {filteredTimeeventTags.length > 0 && (
        <>
          <Typography variant="caption" sx={{ px: 2.5, py: 1.5, fontWeight: 700, color: '#5757b0', display: 'block', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
            {t('tags_dropdown.custom_tags_header') || 'CUSTOM TAGS'}
          </Typography>
          {filteredTimeeventTags.map((tag, index) => {
            // tag is an object like { key: ..., label: ..., value: ..., description: ... }
            // or could be a simple string if tags prop was an array of strings
            const actualTagValue = getTagValue(tag).trim(); // Correctly gets 'someVar' or the string itself
            const displayLabel = tag?.label || actualTagValue; // Prefer tag.label for display
            const tooltipContent = getTooltipContent(tag, displayLabel);
            
            // Get icon - PRIORITIZE backend-provided icon
            let IconComponent;
            if (tag?.icon) {
              // If icon exists, check if it's a string (from backend) or component
              if (typeof tag.icon === 'string') {
                IconComponent = getIconComponent(tag.icon);
              } else {
                IconComponent = tag.icon;
              }
            } else {
              // Only use auto-generated icon as fallback when no icon is provided from backend
              IconComponent = getCustomTagIcon(tag);
            }

            return (
              <Tooltip
                key={tag?.key || tag?.value || tag?.label || `custom-tag-${index}`}
                title={tooltipContent}
                arrow
                componentsProps={{ popper: { sx: { pointerEvents: 'none' } } }}
                enterDelay={200}
              >
                <MenuItem
                  onClick={() => {
                    handleTagSelect(actualTagValue);
                    handleClose(); // Close menu on selection
                  }}
                  sx={{
                    px: 2.5, py: 1.2,
                    '&:hover': { backgroundColor: '#f0f0ff', color: '#5757b0' },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <IconComponent fontSize="small" sx={{ color: '#5757b0' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={displayLabel} // Display user-friendly label or the tag value
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }}
                  />
                </MenuItem>
              </Tooltip>
            );
          })}
          <Divider sx={{ my: 1.5 }} />
        </>
      )}

      {/* WIDGET TAGS - Displayed after custom tags */}
      {filteredWidgetTags.length > 0 && (
        <>
          <Typography variant="caption" sx={{ px: 2.5, py: 1.5, fontWeight: 700, color: '#5757b0', display: 'block', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
            {t('tags_dropdown.widget_tags_header') || 'WIDGET TAGS'}
          </Typography>
          {filteredWidgetTags.map((tag, index) => {
            const actualTagValue = getTagValue(tag).trim();
            const displayLabel = tag?.label || actualTagValue;
            const tooltipContent = getTooltipContent(tag, displayLabel);
            
            // Get icon - PRIORITIZE backend-provided icon
            let IconComponent;
            if (tag?.icon) {
              if (typeof tag.icon === 'string') {
                IconComponent = getIconComponent(tag.icon);
              } else {
                IconComponent = tag.icon;
              }
            } else {
              // Use widget-specific icon as fallback
              IconComponent = getCustomTagIcon(tag);
            }

            return (
              <Tooltip
                key={tag?.key || tag?.value || tag?.label || `widget-tag-${index}`}
                title={tooltipContent}
                arrow
                componentsProps={{ popper: { sx: { pointerEvents: 'none' } } }}
                enterDelay={200}
              >
                <MenuItem
                  onClick={() => {
                    handleTagSelect(actualTagValue);
                    handleClose(); // Close menu on selection
                  }}
                  sx={{
                    px: 2.5, py: 1.2,
                    '&:hover': { backgroundColor: '#f0f0ff', color: '#5757b0' },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <IconComponent fontSize="small" sx={{ color: '#5757b0' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={displayLabel}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }}
                  />
                </MenuItem>
              </Tooltip>
            );
          })}
          <Divider sx={{ my: 1.5 }} />
        </>
      )}

      {/* DEFAULT TAGS - Grouped for clarity */}
      {filteredDefaultTags.length > 0 && (
        <>
          {/* File Metadata Section */}
          {filteredDefaultTags.filter(tag => ['filename', 'file_extension', 'file_folder_path', 'fileid', 'projectid', 'fileContent'].includes(tag.value)).length > 0 && (
            <>
              <Typography variant="caption" sx={{ px: 2.5, py: 1.5, fontWeight: 700, color: '#5757b0', display: 'block', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
                {t('tags_dropdown.file_metadata_header') || 'FILE METADATA'}
              </Typography>
              {filteredDefaultTags
                .filter(tag => ['filename', 'file_extension', 'file_folder_path', 'fileid', 'projectid', 'fileContent'].includes(tag.value)) // fileContent is often API specific
                .map((tag, index) => {
                  const IconComponent = tag.icon;
                  return (
                    <Tooltip key={tag?.key || tag?.value || tag?.label || `file-meta-${index}`} title={tag.description} arrow componentsProps={{ popper: { sx: { pointerEvents: 'none' } } }} enterDelay={200}>
                      <MenuItem
                        onClick={() => {
                          handleTagSelect(tag.value);
                          handleClose(); // Close menu on selection
                        }}
                        sx={{ px: 2.5, py: 1.2, '&:hover': { backgroundColor: '#f0f0ff' }, transition: 'background-color 0.2s ease' }}
                      >
                        {IconComponent && (
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <IconComponent fontSize="small" sx={{ color: '#5757b0' }} />
                          </ListItemIcon>
                        )}
                        <ListItemText primary={tag.label} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }} />
                      </MenuItem>
                    </Tooltip>
                  );
                })}
            </>
          )}

          {/* Date & Time Section */}
          {filteredDefaultTags.filter(tag => [
            'year', 'month', 'day', 'hour', 'minute', 'second',
            'currentyear', 'currentmonth', 'currentday', 'currenthour', 'currentminute', 'currentsecond',
            'filecreationyear', 'filecreationmonth', 'filecreationday', 'filecreationhour', 'filecreationminute', 'filecreationsecond', // Removed duplicate 'filecreationsecond'
            'filemodificationyear', 'filemodificationmonth', 'filemodificationday', 'filemodificationhour', 'filemodificationminute', 'filemodificationsecond' // Removed duplicate 'filemodificationsecond'
          ].includes(tag.value)).length > 0 && (
            <React.Fragment key="datetime-tags-section">
              <Typography variant="caption" sx={{ px: 2.5, py: 1.5, fontWeight: 700, color: '#5757b0', display: 'block', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
                {t('tags_dropdown.datetime_header') || 'DATE & TIME'}
              </Typography>
              {filteredDefaultTags
                .filter(tag => [
                  'year', 'month', 'day', 'hour', 'minute', 'second',
                  'currentyear', 'currentmonth', 'currentday', 'currenthour', 'currentminute', 'currentsecond',
                  'filecreationyear', 'filecreationmonth', 'filecreationday', 'filecreationhour', 'filecreationminute', // 'filecreationsecond' removed if not present in your defaultTags
                  'filemodificationyear', 'filemodificationmonth', 'filemodificationday', 'filemodificationhour', 'filemodificationminute' // 'filemodificationsecond' removed if not present
                ].includes(tag.value))
                .map((tag, index) => {
                  const IconComponent = tag.icon;
                  return (
                    <Tooltip key={tag?.key || tag?.value || tag?.label || `datetime-${index}`} title={tag.description} arrow componentsProps={{ popper: { sx: { pointerEvents: 'none' } } }} enterDelay={200}>
                      <MenuItem
                        onClick={() => {
                          handleTagSelect(tag.value);
                          handleClose(); // Close menu on selection
                        }}
                        sx={{ px: 2.5, py: 1.2, '&:hover': { backgroundColor: '#f0f0ff' }, transition: 'background-color 0.2s ease' }}
                      >
                        {IconComponent && (
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <IconComponent fontSize="small" sx={{ color: '#5757b0' }} />
                          </ListItemIcon>
                        )}
                        <ListItemText primary={tag.label} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }} />
                      </MenuItem>
                    </Tooltip>
                  );
                })}
            </React.Fragment>
          )}
            {/* Other default tags (e.g. text extract, summary) */}
            {filteredDefaultTags.filter(tag => ![
                'filename', 'file_extension', 'file_folder_path', 'fileid', 'projectid', 'fileContent',
                'year', 'month', 'day', 'hour', 'minute', 'second',
                'currentyear', 'currentmonth', 'currentday', 'currenthour', 'currentminute', 'currentsecond',
                'filecreationyear', 'filecreationmonth', 'filecreationday', 'filecreationhour', 'filecreationminute',
                'filemodificationyear', 'filemodificationmonth', 'filemodificationday', 'filemodificationhour', 'filemodificationminute'
            ].includes(tag.value)).length > 0 && (
                <React.Fragment key="other-tags-section">
                <Typography variant="caption" sx={{ px: 2.5, py: 1.5, fontWeight: 700, color: '#5757b0', display: 'block', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
                    {t('tags_dropdown.other_tags_header') || 'OTHER INFO'}
                </Typography>
                {filteredDefaultTags
                    .filter(tag => ![
                        'filename', 'file_extension', 'file_folder_path', 'fileid', 'projectid', 'fileContent',
                        'year', 'month', 'day', 'hour', 'minute', 'second',
                        'currentyear', 'currentmonth', 'currentday', 'currenthour', 'currentminute', 'currentsecond',
                        'filecreationyear', 'filecreationmonth', 'filecreationday', 'filecreationhour', 'filecreationminute',
                        'filemodificationyear', 'filemodificationmonth', 'filemodificationday', 'filemodificationhour', 'filemodificationminute'
                    ].includes(tag.value))
                    .map((tag, index) => {
                      const IconComponent = tag.icon;
                      return (
                        <Tooltip key={tag?.key || tag?.value || tag?.label || `other-${index}`} title={tag.description} arrow componentsProps={{ popper: { sx: { pointerEvents: 'none' } } }} enterDelay={200}>
                          <MenuItem
                            onClick={() => {
                              handleTagSelect(tag.value);
                              handleClose(); // Close menu on selection
                            }}
                            sx={{ px: 2.5, py: 1.2, '&:hover': { backgroundColor: '#f0f0ff' }, transition: 'background-color 0.2s ease' }}
                          >
                            {IconComponent && (
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <IconComponent fontSize="small" sx={{ color: '#5757b0' }} />
                              </ListItemIcon>
                            )}
                            <ListItemText primary={tag.label} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }} />
                          </MenuItem>
                        </Tooltip>
                      );
                    })}
                </React.Fragment>
            )}
        </>
      )}
      {filteredTimeeventTags.length === 0 && filteredWidgetTags.length === 0 && filteredDefaultTags.length === 0 && (
        <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
          {t('tags_dropdown.no_tags_found') || 'No tags found.'}
        </Typography>
      )}
    </Menu>
  );
};

export default TagsDropdown;
